/**
 * ANOTAR UN AMISTOSO QUE YA SE JUGÓ, sin reto de por medio.
 *
 * QUÉ PROBLEMA RESUELVE. Hasta acá, para que un partido quedara registrado
 * había que haberlo pactado ANTES: uno reta, el otro acepta, y recién ahí se
 * anota el marcador. Pero la mayoría de los partidos no pasan así —se juegan
 * porque dos coincidieron en la cancha un martes— y esos quedaban afuera de
 * todo: del historial, de la actividad y del ranking.
 *
 * TRES PASOS Y NO UN FORMULARIO LARGO: contra quién, cómo terminó, listo.
 * Cada uno aparece cuando el anterior está resuelto, así que nunca hay un
 * campo en pantalla que todavía no se puede llenar.
 *
 * NO SUMA HASTA QUE EL OTRO CONFIRME, y la pantalla lo dice ANTES de mandar,
 * no después. Un amistoso suma puntos, así que si bastara con anotarlo
 * cualquiera escribiría «le gané 6-0 6-0» a quien ni estaba. Enterarse de la
 * confirmación recién al final se lee como que algo salió mal.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FRIENDLY_DISCIPLINES,
  type ChallengePlayer,
  type FriendlyDiscipline,
  type MatchScore,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { PlayerPicker } from './PlayerPicker';
import { ScoreEditor } from './ScoreEditor';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';

export function ReportFriendly({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [abierto, setAbierto] = useState(false);
  const [disciplina, setDisciplina] = useState<FriendlyDiscipline>('SINGLES');
  const [rival, setRival] = useState<ChallengePlayer | null>(null);
  /*
    LAS PAREJAS, solo en dobles. Se guardan aunque se cambie a singles: quien
    se equivocó de botón y vuelve no pierde lo que ya eligió, y al mandar se
    ignoran si la disciplina es singles.
  */
  const [miPareja, setMiPareja] = useState<ChallengePlayer | null>(null);
  const [suPareja, setSuPareja] = useState<ChallengePlayer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  /**
   * QUIÉNES YA ESTÁN EN EL PARTIDO, menos el que se está eligiendo ahora.
   *
   * Se le pasa a cada buscador para que no ofrezca a alguien que ya juega. El
   * propio campo se excluye de la lista —si no, al reabrirlo no aparecería la
   * persona que ya tiene elegida y parecería que se borró—.
   */
  function yaEnElPartido(salvo: ChallengePlayer | null): string[] {
    const todos = [session?.user.id ?? null, rival, miPareja, suPareja];
    return todos
      .map((x) => (x === null ? null : typeof x === 'string' ? x : x.userId))
      .filter((id): id is string => id !== null && id !== salvo?.userId);
  }

  /**
   * SI YA SE PUEDE ANOTAR EL MARCADOR. En singles alcanza con el rival; en
   * dobles hacen falta los tres, porque un dobles con tres jugadores no es un
   * partido que se pueda guardar.
   */
  const completo =
    rival !== null && (disciplina === 'SINGLES' || (miPareja !== null && suPareja !== null));

  async function guardar(
    score: MatchScore,
    _noShowId: string | null,
    winner: 'home' | 'away' | null,
  ): Promise<void> {
    if (rival === null || session === null) return;
    setError(null);
    /*
      EL GANADOR SALE DEL MARCADOR, no de otro control. `ScoreEditor` ya lo
      calculó con `checkScore` —la misma función que corre el servidor— y
      volver a deducirlo acá sería una segunda cuenta del mismo hecho.
      «home» es siempre quien anota: el marcador se escribe en su orden.
    */
    const winnerUserId =
      winner === null ? null : winner === 'home' ? session.user.id : rival.userId;

    await api.reportFriendly({
      opponentUserId: rival.userId,
      winnerUserId,
      score,
      // Las dos o ninguna: `completo` ya garantizó que en dobles están las dos.
      ...(disciplina === 'DOUBLES' && miPareja !== null && suPareja !== null
        ? { doubles: { myPartnerId: miPareja.userId, theirPartnerId: suPareja.userId } }
        : {}),
    });
    setListo(true);
  }

  function reiniciar(): void {
    setRival(null);
    setMiPareja(null);
    setSuPareja(null);
    setListo(false);
    setError(null);
    setAbierto(false);
  }

  if (session === null) return <></>;

  if (!abierto) {
    return (
      <p className="button-row">
        <button type="button" className="secondary" onClick={() => setAbierto(true)}>
          {t('friendly.open')}
        </button>
      </p>
    );
  }

  /*
    LO QUE SE VE AL TERMINAR. Dice que falta la confirmación y de quién: sin
    eso, quien anotó ve «listo» y espera puntos que no llegan.
  */
  if (listo && rival !== null) {
    return (
      <section className="report-friendly">
        <Alert kind="success">{t('friendly.savedWaiting', { name: rival.displayName })}</Alert>
        <p className="button-row">
          <button
            type="button"
            onClick={() => {
              reiniciar();
              onDone();
            }}
          >
            {t('common.close')}
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="report-friendly">
      <h3>{t('friendly.title')}</h3>
      <p className="help">{t('friendly.help')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/*
        SINGLES O DOBLES. Dobles se muestra APAGADO en vez de no estar: sin
        él, la pantalla parece no saber que el tenis se juega de a cuatro.
        Apagado y con el motivo al lado se entiende que viene.
      */}
      <div className="field">
        <span className="field-label">{t('friendly.discipline')}</span>
        <div className="choice-row" role="radiogroup" aria-label={t('friendly.discipline')}>
          {FRIENDLY_DISCIPLINES.map((cual) => (
            <label key={cual} className="choice">
              <input
                type="radio"
                name="friendly-discipline"
                value={cual}
                checked={disciplina === cual}
                onChange={() => setDisciplina(cual)}
              />
              <span>{t(`friendly.discipline_${cual}`)}</span>
            </label>
          ))}
        </div>
        {disciplina === 'DOUBLES' && (
          <span className="help">{t('friendly.doublesHelp')}</span>
        )}
      </div>

      {/*
        QUIÉNES JUGARON. En singles es uno; en dobles son tres, y el orden es
        el que se dice en voz alta: «yo con Fulano, contra Mengano y Zutano».

        `exclude` es lo que impide ofrecer a alguien que ya está en el partido.
        Sin eso, la lista te deja elegir dos veces a la misma persona, el
        servidor lo rechaza, y el error correcto es el que nunca se ve porque
        la opción no se ofreció.
      */}
      {disciplina === 'DOUBLES' && (
        <PlayerPicker
          id="friendly-mi-pareja"
          label={t('friendly.myPartner')}
          chosen={miPareja}
          onChoose={setMiPareja}
          exclude={yaEnElPartido(miPareja)}
        />
      )}

      <PlayerPicker
        id="friendly-buscar"
        label={disciplina === 'DOUBLES' ? t('friendly.rival') : t('friendly.who')}
        help={t('friendly.whoHelp')}
        chosen={rival}
        onChoose={setRival}
        exclude={yaEnElPartido(rival)}
      />

      {disciplina === 'DOUBLES' && (
        <PlayerPicker
          id="friendly-su-pareja"
          label={t('friendly.theirPartner')}
          chosen={suPareja}
          onChoose={setSuPareja}
          exclude={yaEnElPartido(suPareja)}
        />
      )}

      {completo && rival !== null && (
        <>
          {/*
            EL AVISO DE LA CONFIRMACIÓN VA ACÁ, ANTES DE MANDAR. Enterarse
            después de guardar de que el partido «no cuenta todavía» se lee
            como que algo falló.
          */}
          <p className="help">{t('friendly.needsConfirm', { name: rival.displayName })}</p>

          {/*
            LOS LADOS LLEVAN LOS DOS NOMBRES EN DOBLES. Con uno solo, el
            marcador dice «Javier» donde juegan Javier y Ana, y quien lo mira
            después no sabe de qué partido es.
          */}
          <ScoreEditor
            home={{
              id: session.user.id,
              name:
                miPareja === null || disciplina === 'SINGLES'
                  ? session.user.displayName
                  : `${session.user.displayName} / ${miPareja.displayName}`,
            }}
            away={{
              id: rival.userId,
              name:
                suPareja === null || disciplina === 'SINGLES'
                  ? rival.displayName
                  : `${rival.displayName} / ${suPareja.displayName}`,
              avatarPath: rival.avatarPath,
            }}
            score={[]}
            noShowId={null}
            // EMPATE SÍ: es un amistoso, y los amistosos pueden quedar
            // empatados. NO SE PRESENTÓ no: un partido que no se jugó no es
            // algo que alguien venga a anotar por su cuenta.
            allowDraw
            allowNoShow={false}
            onSave={guardar}
            onCancel={reiniciar}
          />
        </>
      )}
    </section>
  );
}
